import moment from "moment";
import { TFunction } from "i18next";
import { TimelineEntry, TimelineUser } from "../../../models/EventTimeline";

/**
 * Interpolation values for an entry's label sentence, keyed by the placeholder
 * name used in the i18n string (e.g. `{{consultant}}`, `{{name}}`).
 *
 * Supplied per entity: what a job event needs to say ("{{consultant}} ansökte")
 * has nothing in common with what a consultant event needs ("Certifikat
 * borttaget: {{name}}").
 */
export type LabelValues = { [placeholder: string]: string };

export type GetLabelValues = (entry: TimelineEntry, t: TFunction) => LabelValues;

/**
 * User id to expose as `data-contact` (opens the global ContactContextMenu on
 * right-click), or undefined when no menu should open. Only consultants and
 * customer users are contactable — internal users (admins/consultant managers)
 * get no menu, as does anyone whose roles are unknown (legacy entries where
 * the user could not be resolved).
 */
export function getTimelineContactId(user: TimelineUser | undefined): string | undefined {
	if (!user?.roles?.length) return undefined;

	const isContactable = user.roles.every(
		(role) => role === "consultant" || role === "customer_user"
	);

	return isContactable ? user.id : undefined;
}

export function formatUserName(user: TimelineUser | undefined, t: TFunction): string {
	if (!user) return t("eventTimeline.system");

	const name = [user.firstName, user.lastName].filter(Boolean).join(" ");

	return name || user.id;
}

/**
 * "via AI-assistenten" for a change the user made through a channel other than
 * the dashboard. Channels are a curated backend list, so an unknown one shows
 * its raw value rather than nothing: "via <channel>" still says more than an
 * unqualified actor name.
 */
export function formatVia(via: string, t: TFunction): string {
	return t("eventTimeline.via", {
		channel: t(`eventTimeline.channelLabel.${via}`, { defaultValue: via }),
	});
}

/**
 * Tailwind class for the timeline dot. Maps are passed in rather than built as
 * strings so Tailwind's static scan picks the classes up.
 *
 * A source map takes precedence over the type map: a merged timeline colours by
 * where the entry came from first (job time reports are all teal regardless of
 * their milestone type).
 */
export function getDotClass(
	entry: TimelineEntry,
	dotClassByType: { [type: string]: string } = {},
	dotClassBySource: { [source: string]: string } = {}
): string {
	if (entry.source && dotClassBySource[entry.source]) return dotClassBySource[entry.source];

	return dotClassByType[entry.type] ?? "bg-gray-400";
}

/**
 * The one-line Swedish sentence for an entry. Unknown/legacy types fall back
 * to a generic label so old data never breaks the timeline.
 */
export function getEntryLabel(
	entry: TimelineEntry,
	t: TFunction,
	i18nPrefix: string,
	values: LabelValues = {}
): string {
	const known = t(`${i18nPrefix}.event.${entry.type}` as any, {
		defaultValue: "",
		...values,
	});

	return known || t("eventTimeline.event.UNKNOWN");
}

/**
 * Field paths are dotted (`profile.employment.started`) and i18next treats a dot
 * as key nesting, so paths are flattened to underscores for lookup:
 * `eventTimeline.consultant.fields.profile_employment_started`. Undotted job
 * fields are unaffected.
 */
export function getFieldLabel(field: string, t: TFunction, i18nPrefix: string): string {
	return t(`${i18nPrefix}.fields.${field.replace(/\./g, "_")}` as any, { defaultValue: field });
}

/**
 * Fields never rendered with their values, whatever the entry carries.
 *
 * The backend already drops these at write time, so new entries arrive with
 * `redacted: true` and no values. This list is the belt to that pair of braces:
 * it also covers entries written before the redaction landed, and any future
 * audited path someone adds without thinking about it.
 */
const REDACTED_FIELDS = new Set([
	"profile.socialSecurityNumber",
	"profile.bank.accountNumber",
	"profile.bank.clearingNumber",
]);

export function isRedactedChange(change: { field: string; redacted?: boolean }): boolean {
	return !!change.redacted || REDACTED_FIELDS.has(change.field);
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Human string for a change value, or null when the value is not worth
 * rendering (objects/arrays) — the row then shows only the field label.
 */
export function formatChangeValue(value: unknown, t: TFunction): string | null {
	if (value === undefined || value === null || value === "") return "–";

	if (typeof value === "boolean") return value ? t("eventTimeline.yes") : t("eventTimeline.no");

	if (typeof value === "number") return String(value);

	if (typeof value === "string") {
		if (ISO_DATE_PATTERN.test(value)) return moment(value).format("D MMM YYYY HH:mm");

		return value;
	}

	return null;
}

/**
 * Enum-valued fields whose raw codes have a display label, keyed by the
 * flattened field path. Unknown values fall back to the raw code.
 */
const ENUM_VALUE_LABELS: { [field: string]: (value: string, t: TFunction) => string } = {
	invoiceDetails_deliveryMethod: (value, t) =>
		t(`deliveryMethod.label.${value}` as any, { defaultValue: value }),
	invoiceDetails_deliveryManagedBy: (value) => (value === "FORTNOX" ? "Fortnox" : value),
};

/** Display value for an enum code on a known field; other values pass through. */
export function translateEnumValue(field: string, value: unknown, t: TFunction): unknown {
	const toLabel = ENUM_VALUE_LABELS[field.replace(/\./g, "_")];

	if (!toLabel || typeof value !== "string" || !value) return value;

	return toLabel(value, t);
}

/** Resolves a requirement key to its org-taxonomy display name. */
export type ResolveRequirementName = (
	kind: "certification" | "tag",
	key: string
) => string | undefined;

/** Resolves a branch/service key pair to the org competence tree's display name. */
export type ResolveCompetenceName = (branch: string, service: string) => string | undefined;

/**
 * Human string for a `competence` change side ({branch, service, skills?}),
 * resolved against the org competence tree. Falls back to the raw service key
 * so removed taxonomy entries still render.
 */
export function formatCompetence(value: unknown, resolve?: ResolveCompetenceName): string | null {
	const competence = value as { branch?: string; service?: string } | undefined;

	if (!competence?.service) return null;

	return (
		(competence.branch && resolve?.(competence.branch, competence.service)) || competence.service
	);
}

export type RequirementDiffItem = {
	kind: "certification" | "tag";
	key: string;
};

export type RequirementDiff = {
	added: RequirementDiffItem[];
	removed: RequirementDiffItem[];
};

function toRequirementItems(value: unknown): RequirementDiffItem[] {
	const requirements = (value ?? {}) as {
		certifications?: { typeKey?: string }[];
		tags?: { key?: string }[];
	};

	return [
		...(requirements.certifications ?? [])
			.filter((c) => c.typeKey)
			.map((c) => ({ kind: "certification" as const, key: c.typeKey! })),
		...(requirements.tags ?? [])
			.filter((tag) => tag.key)
			.map((tag) => ({ kind: "tag" as const, key: tag.key! })),
	];
}

/**
 * Set-diff of a `requirements` change ({certifications, tags} on both sides)
 * into added/removed entries, so the timeline can render "+Truckkort −HLR"
 * instead of a generic "Krav ändrades".
 */
export function diffRequirements(from: unknown, to: unknown): RequirementDiff {
	const fromItems = toRequirementItems(from);
	const toItems = toRequirementItems(to);

	const itemKey = ({ kind, key }: RequirementDiffItem) => `${kind}:${key}`;
	const fromKeys = new Set(fromItems.map(itemKey));
	const toKeys = new Set(toItems.map(itemKey));

	return {
		added: toItems.filter((item) => !fromKeys.has(itemKey(item))),
		removed: fromItems.filter((item) => !toKeys.has(itemKey(item))),
	};
}
